import FeatherIcon from 'feather-icons-react';
import React, { useState } from 'react';
import * as i18next from 'i18next';
import { IConsentInfoAccount } from '../../types/IConsentInfoAccount';
import { maskAccountNumber, mapToSubAccountType } from '../../domain/services/BankAccountModifierService';

interface IProps {
  t: i18next.TFunction;
  accounts?: IConsentInfoAccount[];
  bankName: string;
  isActive: boolean;
  lastUpdatedDate: Date;
  reconfirmByDate?: Date;
  bankLogoUrl?: string;
}

const ReconnectionAccountPageContent: React.FC<IProps> = (props: IProps) => {
  const { t, bankName, accounts, isActive, lastUpdatedDate, reconfirmByDate, bankLogoUrl } = props;
  const [isAccountsExpanded, setIsAccountsExpanded] = useState(true);

  const accountsLabel = () => {
    const count = accounts?.length || 0;

    const label = isAccountsExpanded ? 'Hide ' : 'Show ';
    return count === 1 ? label + 'account' : label + 'accounts';
  };

  const lastUpdated = lastUpdatedDate?.toLocaleDateString(navigator.language, {
    year: 'numeric',
    month: 'long',
    day: '2-digit'
  });

  const reconfirmationDate = isActive
    ? reconfirmByDate?.toLocaleDateString(navigator.language, {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      })
    : lastUpdatedDate?.toLocaleDateString(navigator.language, {
        year: 'numeric',
        month: 'long',
        day: '2-digit'
      });

  const toggleAccountsExpansion = () => {
    setIsAccountsExpanded(!isAccountsExpanded);
  };

  return (
    <>
      <div className="border border-gray-300 round rounded-lg p-4" data-testid="reconnection-account-page-content">
        <div>
          <div className="flex direction-row justify-between">
            <div className="flex flex-col">
              <p className="!m-0 uppercase  text-xs md:text-sm  tracking-widest">{t<string>('Your Bank')}</p>
              {bankLogoUrl ? (
                <div className="w-[140px] sm:w-30 md:w-40 lg:w-40">
                  <img src={bankLogoUrl} alt={bankName || 'Bank logo'} />
                </div>
              ) : (
                <p className="!m-0 uppercase text-xs md:text-sm tracking-widest">{bankName}</p>
              )}
            </div>
            <div className="flex direction-row mb-5">
              {isActive ? (
                <>
                  <FeatherIcon icon="lock" className="text-gray-500" />
                  <FeatherIcon icon="check-circle" className="ml-2 text-positive" data-testid="consent-active" />
                </>
              ) : (
                <div className="flex items-center">
                  <FeatherIcon icon="x-circle" className="text-negative" data-testid="consent-not-active" />
                  {!isActive && <p className="ml-1 text-negative text-sm">{t<string>('Expired')}</p>}
                </div>
              )}
            </div>
          </div>

          {accounts && accounts?.length > 0 && (
            <>
              <div className="flex justify-end mb-2">
                <button
                  onClick={toggleAccountsExpansion}
                  className="flex items-center text-sm md:text-sm text-gray-600 hover:text-gray-800 "
                  type="button"
                >
                  <span className="mr-1">{t<string>(accountsLabel())}</span>
                  <FeatherIcon icon={isAccountsExpanded ? 'chevron-up' : 'chevron-down'} className="w-3" />
                </button>
              </div>

              {isAccountsExpanded && (
                <div className="bg-gray-100 rounded-lg p-4">
                  {accounts?.map((account, idx) => (
                    <div key={idx} className="mb-2 last:mb-0">
                      <div
                        className="text-sm uppercase font-bold"
                        aria-label={`${bankName} ${t<string>(mapToSubAccountType(account.accountSubType))} account`}
                      >
                        {t<string>(mapToSubAccountType(account.accountSubType))}
                      </div>
                      <div className="flex flex-row">
                        <p className="text-sm md:text-sm text-gray-600">{t<string>('Account no:')} </p>
                        <p className="text-sm md:text-sm text-primary-2">
                          {maskAccountNumber(account.identifiers.accountNumber)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}

          <div className="mt-4">
            <p className="text-xs md:text-sm text-gray-700">{`${t<string>('Last Updated: ')}${lastUpdated}`}</p>
            <p className="text-xs md:text-sm text-gray-700 ">{`${t<string>(
              isActive ? 'Reconfirm connection by' : 'Expired'
            )}: ${reconfirmationDate}`}</p>
          </div>
        </div>
      </div>
    </>
  );
};

export default ReconnectionAccountPageContent;
